import type { Vec2 } from '../../core/model';

/** screen = canvas * scale + offset (CSS pixels inside the editor element). */
export interface Viewport {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 8;

export function fitViewport(container: Vec2, canvas: Vec2, padding = 16): Viewport {
  const availW = Math.max(1, container.x - padding * 2);
  const availH = Math.max(1, container.y - padding * 2);
  const scale = Math.max(MIN_ZOOM / 10, Math.min(availW / Math.max(1, canvas.x), availH / Math.max(1, canvas.y)));
  return { scale, offsetX: (container.x - canvas.x * scale) / 2, offsetY: (container.y - canvas.y * scale) / 2 };
}

export function centeredViewport(container: Vec2, canvas: Vec2, scale: number): Viewport {
  return { scale, offsetX: (container.x - canvas.x * scale) / 2, offsetY: (container.y - canvas.y * scale) / 2 };
}

/** Zoom by `factor`, keeping the canvas point under `anchor` (screen) fixed. */
export function zoomAt(viewport: Viewport, factor: number, anchor: Vec2): Viewport {
  const scale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, viewport.scale * factor));
  const applied = scale / viewport.scale;
  return {
    scale,
    offsetX: anchor.x - (anchor.x - viewport.offsetX) * applied,
    offsetY: anchor.y - (anchor.y - viewport.offsetY) * applied,
  };
}

export function screenToCanvas(viewport: Viewport, point: Vec2): Vec2 {
  return { x: (point.x - viewport.offsetX) / viewport.scale, y: (point.y - viewport.offsetY) / viewport.scale };
}

export function canvasToScreen(viewport: Viewport, point: Vec2): Vec2 {
  return { x: point.x * viewport.scale + viewport.offsetX, y: point.y * viewport.scale + viewport.offsetY };
}
